import React, { useState, useEffect } from 'react';
import TokenUsageByModelChart from './TokenUsageByModelChart';
import MajorTopicDiscussed from './MajorTopicDiscussed';
import TopUserActivity from './TopUserActivity';
import DateRangeFilter from './DateRangeFilter';
import { fetchTokenUsageByModel, fetchMajorTopicDiscussed, fetchTopUsers } from '../../services/api/usageMetrics';
import { TokenUsageUser, UserData, MajorTopic } from '../../types/usageMetrics';

interface MetricsChartsContainerProps {
  assistant: any;
  isUsageMetricsModalOpen: boolean;
}

type TokenUsageByModelEntry = {
  date: string;
  model_used: string;
  count: number;
};

// Consistent loading component for all charts
const LoadingComponent = () => (
  <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition">
    <div className="flex justify-center items-center h-80">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  </div>
);

const COLORS = {
  primary: '#4F46E5', // Indigo
  secondary: '#8B5CF6', // Purple
};

const MetricsChartsContainer: React.FC<MetricsChartsContainerProps> = ({
  assistant,
  isUsageMetricsModalOpen
}) => {
  const getFormattedDate = (date: Date): string => {
    return date.toISOString().split('T')[0]; // returns YYYY-MM-DD
  };

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  // Token usage chart state
  const [tokenData, setTokenData] = useState<TokenUsageByModelEntry[]>([]);
  const [fromDate, setFromDate] = useState<string>(getFormattedDate(sevenDaysAgo));
  const [toDate, setToDate] = useState<string>(getFormattedDate(today));
  const [isTokenLoading, setIsTokenLoading] = useState<boolean>(false);

  // Topics state
  const [topics, setMajorTopic] = useState<MajorTopic[]>([]);
  const [isTopicsLoading, setIsTopicsLoading] = useState<boolean>(false);

  // Top users state
  const [topUsersData, setTopUsersData] = useState<UserData[]>([]);
  const [topUserStartDate, setTopUserStartDate] = useState<string>(getFormattedDate(sevenDaysAgo));
  const [topUserEndDate, setTopUserEndDate] = useState<string>(getFormattedDate(today));
  const [isTopUserLoading, setIsTopUserLoading] = useState<boolean>(false);

  // Load token usage data
  useEffect(() => {
    const loadUsageData = async () => {
      setIsTokenLoading(true);
      try {
        const data = await fetchTokenUsageByModel(
          assistant?.app_id,
          fromDate,
          toDate
        );
        setTokenData(data);
      } catch (error) {
        console.error("Error fetching token usage data:", error);
      } finally {
        setIsTokenLoading(false);
      }
    };

    if (assistant?.app_id && String(isUsageMetricsModalOpen) === 'true') {
      loadUsageData();
    }
  }, [assistant?.app_id, fromDate, toDate, isUsageMetricsModalOpen]);

  // Load topics data
  useEffect(() => {
    const loadData = async () => {
      setIsTopicsLoading(true);
      try {
        const data = await fetchMajorTopicDiscussed(assistant?.app_id);
        if (data) setMajorTopic(data);
      } catch (error) {
        console.error("Error fetching topics data:", error);
      } finally {
        setIsTopicsLoading(false);
      }
    };

    if (isUsageMetricsModalOpen) {
      loadData();
    }
  }, [assistant?.app_id, isUsageMetricsModalOpen]);

  // Load top users data
  useEffect(() => {
    const loadUsageData = async () => {
      setIsTopUserLoading(true);
      try {
        const data = await fetchTopUsers(
          assistant?.app_id,
          topUserStartDate,
          topUserEndDate
        );
        setTopUsersData(data);
      } catch (error) {
        console.error("Error fetching top users data:", error);
      } finally {
        setIsTopUserLoading(false);
      }
    };

    if (assistant?.app_id && isUsageMetricsModalOpen) {
      loadUsageData();
    }
  }, [assistant?.app_id, topUserStartDate, topUserEndDate, isUsageMetricsModalOpen]);

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setFromDate(newStartDate);
    setToDate(newEndDate);
  };

  const handleTopUserDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setTopUserStartDate(newStartDate);
    setTopUserEndDate(newEndDate);
  };

  return (
    <>
      {/* Token Usage Chart */}
      <div>
        {isTokenLoading ? (
          <LoadingComponent />
        ) : (
          <TokenUsageByModelChart
            initialTokenData={tokenData}
            colors={{
              primary: COLORS.primary,
              secondary: COLORS.secondary
            }}
            onDateRangeChange={handleDateRangeChange}
            fromDate={fromDate}
            toDate={toDate}
          />
        )}
      </div>

      {/* Major Topics */}
      <div>
        {isTopicsLoading ? (
          <LoadingComponent />
        ) : (
          <MajorTopicDiscussed topics={topics} />
        )}
      </div>

      {/* User Activity Section */}
      <div className="lg:col-span-2">
        {isTopUserLoading ? (
          <LoadingComponent />
        ) : (
          <TopUserActivity 
            userData={topUsersData} 
            onDateRangeChange={handleTopUserDateRangeChange} 
            assistant={assistant} 
            fromDate={topUserStartDate}
            toDate={topUserEndDate}
          />
        )}
      </div>
    </>
  );
};

export default MetricsChartsContainer;